import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bot, Send, User as UserIcon, Sparkles, RefreshCcw, Trash2, ShieldCheck, ChevronRight, History, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PROMPTS = [
    "What needs my attention today?",
    "Which cards are due in the next 7 days?",
    "How much did I spend on fuel this month?",
    "Which cards are above 70% utilization?",
];

/**
 * Very small markdown-ish renderer: bold, bullets, line breaks. Kept purposely
 * simple so we don't pull a full markdown lib into the drawer/page.
 */
function renderText(text) {
    const lines = (text || "").split("\n");
    const out = [];
    let listBuf = [];
    const flushList = () => {
        if (listBuf.length === 0) return;
        out.push(
            <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-1.5">
                {listBuf.map((l, i) => (
                    <li key={`li-${i}-${l.slice(0, 12)}`}>{renderInline(l)}</li>
                ))}
            </ul>,
        );
        listBuf = [];
    };
    lines.forEach((raw, idx) => {
        const line = raw.trimEnd();
        const bullet = line.match(/^\s*[-•*]\s+(.*)/);
        const h3 = line.match(/^###\s+(.*)/);
        const h2 = line.match(/^##\s+(.*)/);
        const h1 = line.match(/^#\s+(.*)/);
        const hr = /^\s*(---+|___+|\*\*\*+)\s*$/.test(line);
        if (bullet) {
            listBuf.push(bullet[1]);
            return;
        }
        flushList();
        if (hr) {
            out.push(<div key={`hr-${idx}`} className="my-2 border-t border-stone-200" />);
        } else if (h1 || h2) {
            const t = (h1 || h2)[1];
            out.push(
                <div key={`h-${idx}`} className="font-display text-lg text-stone-900 mt-2 mb-1">
                    {renderInline(t)}
                </div>,
            );
        } else if (h3) {
            out.push(
                <div key={`h3-${idx}`} className="eyebrow text-stone-700 mt-2 mb-1">
                    {renderInline(h3[1])}
                </div>,
            );
        } else if (line === "") {
            out.push(<div key={`br-${idx}`} className="h-1.5" />);
        } else {
            out.push(<div key={`p-${idx}`}>{renderInline(line)}</div>);
        }
    });
    flushList();
    return out;
}
function renderInline(s) {
    // Handle **bold** first, then `code`
    const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((p, i) => {
        const k = `${i}-${p.slice(0, 16)}`;
        if (/^\*\*[^*]+\*\*$/.test(p)) {
            return <strong key={k} className="font-medium text-stone-900">{p.slice(2, -2)}</strong>;
        }
        if (/^`[^`]+`$/.test(p)) {
            return <code key={k} className="px-1 py-0.5 rounded bg-stone-100 text-stone-800 text-[0.8em]">{p.slice(1, -1)}</code>;
        }
        return <span key={k}>{p}</span>;
    });
}

export default function CopilotPanel({ compact = false }) {
    const [messages, setMessages] = useState([]); // {role, content}
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [suggestions, setSuggestions] = useState(DEFAULT_PROMPTS);
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    // Model toggle — persisted locally so it survives a refresh.
    const [modelId, setModelId] = useState(() => {
        try { return localStorage.getItem("vault.copilot_model") || "claude"; }
        catch { return "claude"; }
    });
    const [models, setModels] = useState([]);
    const listRef = useRef(null);
    const abortRef = useRef(null);

    // Pre-fill from ?q= for palette / marketing deep-links, e.g.
    // /copilot?q=What+did+I+spend+on+Zomato+last+quarter
    useEffect(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            const q = sp.get("q");
            if (q) setDraft(q);
        } catch { /* noop */ }
    }, []);

    const loadSessions = useCallback(() => {
        api.get("/ai/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {});
    }, []);

    useEffect(() => {
        api.get("/ai/chat/suggestions")
            .then((r) => {
                if (Array.isArray(r.data?.prompts) && r.data.prompts.length) {
                    setSuggestions(r.data.prompts);
                }
            })
            .catch(() => {});
        api.get("/ai/models")
            .then((r) => setModels(Array.isArray(r.data?.models) ? r.data.models : []))
            .catch(() => {});
        loadSessions();
    }, [loadSessions]);

    // Persist model choice
    useEffect(() => {
        try { localStorage.setItem("vault.copilot_model", modelId); } catch { /* noop */ }
    }, [modelId]);

    const selectedModel = useMemo(
        () => models.find((m) => m.id === modelId) || null,
        [models, modelId],
    );

    const openSession = async (sid) => {
        try {
            const { data } = await api.get(`/ai/sessions/${sid}/messages`);
            setSessionId(sid);
            setMessages((data.messages || []).map((m) => ({
                role: m.role, content: m.content, model: m.model,
            })));
            setHistoryOpen(false);
        } catch { /* ignore */ }
    };

    const deleteSession = async (sid, e) => {
        e?.stopPropagation();
        try {
            await api.delete(`/ai/sessions/${sid}`);
            if (sid === sessionId) {
                setSessionId(null);
                setMessages([]);
            }
            loadSessions();
        } catch { /* ignore */ }
    };

    // Autoscroll to the bottom whenever messages change or during streaming
    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, busy]);

    const stopStream = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);

    const send = useCallback(
        async (text) => {
            const q = (text ?? draft).trim();
            if (!q || busy) return;
            const nextMsgs = [...messages, { role: "user", content: q }];
            setMessages(nextMsgs);
            setDraft("");
            setBusy(true);

            // Placeholder assistant message we stream into
            setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

            const controller = new AbortController();
            abortRef.current = controller;

            try {
                const resp = await fetch(`${BACKEND_URL}/api/ai/chat`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ messages: nextMsgs, model: modelId }),
                    signal: controller.signal,
                });
                if (!resp.ok || !resp.body) {
                    const errTxt = await resp.text().catch(() => "");
                    throw new Error(errTxt || `HTTP ${resp.status}`);
                }
                const reader = resp.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                let acc = "";
                let streamError = null;
                for (;;) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const parts = buffer.split("\n\n");
                    buffer = parts.pop() || "";
                    for (const part of parts) {
                        const line = part.trim();
                        if (!line.startsWith("data:")) continue;
                        try {
                            const evt = JSON.parse(line.slice(5).trim());
                            if (typeof evt.delta === "string") {
                                acc += evt.delta;
                                setMessages((prev) => {
                                    const copy = [...prev];
                                    copy[copy.length - 1] = { role: "assistant", content: acc };
                                    return copy;
                                });
                            } else if (evt.error) {
                                streamError = evt.error;
                            }
                        } catch {
                            /* ignore malformed lines */
                        }
                    }
                }
                if (streamError && !acc) {
                    throw new Error(streamError);
                }
                // Ask NEXUS v2 — fetch action chips based on Q + final answer
                try {
                    const chipsResp = await api.post("/ai/chat/action-chips", {
                        question: q, answer: acc,
                    });
                    const chips = chipsResp.data?.chips || [];
                    if (chips.length > 0) {
                    setMessages((prev) => {
                        const copy = [...prev];
                        copy[copy.length - 1] = { ...copy[copy.length - 1], chips, followups: (chipsResp.data?.followups || []).slice(0, 3) };
                        return copy;
                    });
                }
                } catch {
                    /* chips are best-effort */
                }
                // Persist the exchange so it lands in Sessions history
                try {
                    const persistResp = await api.post("/ai/chat/persist", {
                        question: q, answer: acc,
                        session_id: sessionId,
                        model: modelId,
                    });
                    if (persistResp.data?.session_id && persistResp.data.session_id !== sessionId) {
                        setSessionId(persistResp.data.session_id);
                    }
                    loadSessions();
                } catch { /* persistence is best-effort */ }
            } catch (e) {
                if (e.name === "AbortError") {
                    setMessages((prev) => {
                        const copy = [...prev];
                        const last = copy[copy.length - 1];
                        if (last && last.role === "assistant" && !last.content) {
                            copy.pop();
                        }
                        return copy;
                    });
                } else {
                    setMessages((prev) => {
                        const copy = [...prev];
                        copy[copy.length - 1] = {
                            role: "assistant",
                            content: `Sorry — I couldn't complete that request. (${e.message || "network error"})`,
                            error: true,
                        };
                        return copy;
                    });
                }
            } finally {
                setBusy(false);
                abortRef.current = null;
            }
        },
        [draft, busy, messages, sessionId, loadSessions, modelId],
    );

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const reset = () => {
        stopStream();
        setMessages([]);
        setDraft("");
        setSessionId(null);
    };

    const hasChat = messages.length > 0;

    const emptyState = useMemo(
        () => (
            <div className="flex flex-col items-center justify-center text-center px-6 py-10">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-stone-900 to-stone-700 text-white flex items-center justify-center shadow-sm">
                    <Sparkles size={22} strokeWidth={1.6} />
                </div>
                <div className="font-display text-2xl text-stone-900 mt-4">Finance Copilot</div>
                <p className="text-sm text-stone-500 mt-2 max-w-sm leading-relaxed">
                    Ask anything about your cards, expenses, statements, dues, and payments.
                    I only see the aggregated context on your dashboard — never CVV, OTP or PIN.
                </p>
                <div className="mt-6 w-full grid gap-2 max-w-md">
                    {suggestions.slice(0, 4).map((p) => (
                        <button
                            key={p}
                            className="text-left text-sm border border-stone-200 rounded-lg px-3 py-2 hover:border-stone-900 hover:bg-stone-50 transition-colors"
                            onClick={() => send(p)}
                            data-testid={`copilot-suggest-${p.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] text-stone-500">
                    <ShieldCheck size={12} /> Answers use your dashboard data only. No sensitive fields are ever shared.
                </div>
            </div>
        ),
        [suggestions, send],
    );

    return (
        <div className={`flex flex-col ${compact ? "h-full" : "h-[calc(100vh-11rem)]"}`} data-testid="copilot-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-stone-900 text-white flex items-center justify-center">
                        <Bot size={16} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-stone-900 leading-tight">Finance Copilot</div>
                        <div className="text-[11px] text-stone-500 leading-tight">
                            Grounded in your dashboard — {selectedModel?.label || (modelId === "gpt" ? "GPT 5.6 Sol" : "Claude Sonnet 4.6")}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {/* Model toggle — user picks Claude vs GPT per message */}
                    {models.length > 1 && (
                        <div
                            className="inline-flex items-center rounded-full border border-stone-200 p-0.5 mr-1 bg-stone-50"
                            data-testid="copilot-model-toggle"
                            role="tablist"
                            aria-label="Choose AI model"
                        >
                            {models.map((m) => {
                                const active = m.id === modelId;
                                return (
                                    <button
                                        key={m.id}
                                        onClick={() => setModelId(m.id)}
                                        disabled={busy}
                                        className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-all ${
                                            active
                                                ? "bg-stone-900 text-white shadow-sm"
                                                : "text-stone-600 hover:text-stone-900"
                                        }`}
                                        data-testid={`copilot-model-${m.id}`}
                                        title={m.strengths}
                                        role="tab"
                                        aria-selected={active}
                                    >
                                        {m.id === "gpt" ? "GPT 5.6" : "Claude 4.6"}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {sessions.length > 0 && (
                        <div className="relative">
                            <button
                                onClick={() => setHistoryOpen((v) => !v)}
                                className="topbar-btn"
                                data-testid="copilot-history-btn"
                                title="Past chats"
                            >
                                <History size={13} /> History
                            </button>
                            {historyOpen && (
                                <div className="absolute right-0 top-full mt-1 z-40 w-72 rounded-lg border border-stone-200 bg-white shadow-lg max-h-96 overflow-y-auto"
                                     data-testid="copilot-history-menu">
                                    {sessions.map((s) => {
                                        const lm = (s.last_model || "").toLowerCase();
                                        const modelPill = lm === "gpt"
                                            ? { label: "GPT 5.6", cls: "bg-amber-50 text-amber-800 border-amber-200" }
                                            : lm === "claude"
                                                ? { label: "Claude 4.6", cls: "bg-stone-100 text-stone-700 border-stone-200" }
                                                : null;
                                        return (
                                            <button key={s.id}
                                                    onClick={() => openSession(s.id)}
                                                    className={`w-full text-left px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-0 flex items-start justify-between gap-2 ${s.id === sessionId ? "bg-stone-50" : ""}`}
                                                    data-testid={`copilot-session-${s.id}`}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[13px] font-medium text-stone-900 truncate">{s.title}</div>
                                                    <div className="text-[10.5px] text-stone-500 mt-0.5 flex items-center gap-1.5">
                                                        <span>{s.message_count} msg · {new Date(s.updated_at).toLocaleDateString()}</span>
                                                        {modelPill && (
                                                            <span
                                                                className={`inline-flex items-center px-1.5 py-[1px] rounded border text-[9.5px] font-semibold uppercase tracking-wider ${modelPill.cls}`}
                                                                data-testid={`copilot-session-model-${s.id}`}
                                                            >
                                                                {modelPill.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <button onClick={(e) => deleteSession(s.id, e)} className="text-stone-400 hover:text-red-600 p-0.5">
                                                    <Trash2 size={11} />
                                                </button>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {hasChat && (
                        <button
                            onClick={reset}
                            className="topbar-btn"
                            data-testid="copilot-reset-btn"
                            title="New chat"
                        >
                            <Plus size={13} /> New
                        </button>
                    )}
                </div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto thin-scroll bg-stone-50" data-testid="copilot-messages">
                {!hasChat ? (
                    emptyState
                ) : (
                    <div className="px-4 py-4 space-y-3">
                        {messages.map((m, i) => (
                            <Bubble key={`msg-${i}-${m.role}`} role={m.role} content={m.content} error={m.error} chips={m.chips} followups={m.followups} onFollowup={send} isLast={i === messages.length - 1} busy={busy} model={m.model} />
                        ))}
                    </div>
                )}
            </div>

            {/* Composer */}
            <div className="border-t border-stone-200 bg-white p-3">
                {hasChat && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {suggestions.slice(0, 3).map((p) => (
                            <button
                                key={p}
                                onClick={() => send(p)}
                                className="text-[11px] px-2.5 py-1 border border-stone-200 rounded-full text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-colors"
                                disabled={busy}
                                data-testid={`copilot-chip-${p.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={2}
                        placeholder="Ask about outstanding, dues, categories, cards…"
                        className="flex-1 resize-none border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-stone-900 transition-colors bg-white"
                        disabled={busy}
                        data-testid="copilot-input"
                    />
                    {busy ? (
                        <button
                            onClick={stopStream}
                            className="h-10 px-3 rounded-lg bg-stone-100 text-stone-700 border border-stone-200 hover:bg-stone-200 text-sm inline-flex items-center gap-1.5"
                            data-testid="copilot-stop-btn"
                        >
                            <RefreshCcw size={14} className="animate-spin" /> Stop
                        </button>
                    ) : (
                        <button
                            onClick={() => send()}
                            disabled={!draft.trim()}
                            className="h-10 px-3 rounded-lg bg-stone-900 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-stone-800 transition-colors"
                            data-testid="copilot-send-btn"
                        >
                            <Send size={14} /> Ask
                        </button>
                    )}
                </div>
                <div className="mt-1.5 text-[10px] text-stone-400 leading-tight flex items-center justify-between gap-2">
                    <span>Copilot uses aggregated dashboard data. It never sees CVV, OTP, PIN or banking passwords.</span>
                    <a
                        href="https://app.emergent.sh/profile"
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 text-stone-500 hover:text-stone-900 border border-stone-200 rounded-full px-2 py-0.5 font-medium"
                        data-testid="copilot-cost-meter"
                        title="Manage Universal LLM Key balance"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Key balance ↗
                    </a>
                </div>
            </div>
        </div>
    );
}

function Bubble({ role, content, error, chips, followups, onFollowup, isLast, busy, model }) {
    const isUser = role === "user";
    const showCursor = isLast && !isUser && busy;
    const modelLabel = model === "gpt" ? "GPT 5.6" : model === "claude" ? "Claude 4.6" : null;
    return (
        <div className={`flex gap-2.5 ${isUser ? "justify-end" : "justify-start"}`}>
            {!isUser && (
                <div className="w-7 h-7 rounded-full bg-stone-900 text-white flex items-center justify-center shrink-0">
                    <Bot size={13} strokeWidth={1.8} />
                </div>
            )}
            <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} max-w-[85%]`}>
                {!isUser && modelLabel && content && (
                    <div
                        className={`text-[10px] font-semibold uppercase tracking-widest mb-1 px-1.5 py-0.5 rounded ${
                            model === "gpt"
                                ? "bg-amber-50 text-amber-800 border border-amber-200"
                                : "bg-stone-100 text-stone-700 border border-stone-200"
                        }`}
                        data-testid={`copilot-bubble-model-${model}`}
                    >
                        {modelLabel}
                    </div>
                )}
                <div
                    className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                        isUser
                            ? "bg-stone-900 text-white rounded-br-sm"
                            : error
                                ? "bg-red-50 text-red-800 border border-red-200 rounded-bl-sm"
                                : "bg-white text-stone-800 border border-stone-200 rounded-bl-sm"
                    }`}
                    data-testid={`copilot-bubble-${role}`}
                >
                    {content ? renderText(content) : (
                        <span className="inline-flex items-center gap-1.5 text-stone-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-pulse" />
                            <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-pulse [animation-delay:150ms]" />
                            <span className="w-1.5 h-1.5 rounded-full bg-stone-400 animate-pulse [animation-delay:300ms]" />
                        </span>
                    )}
                    {showCursor && content && (
                        <span className="inline-block w-1.5 h-3 bg-stone-500 ml-0.5 align-middle animate-pulse" />
                    )}
                </div>
                {!isUser && chips && chips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="copilot-action-chips">
                        {chips.map((chip, ci) => {
                            const tone = {
                                warning: "text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100",
                                success: "text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100",
                                default: "text-stone-700 border-stone-200 bg-white hover:bg-stone-100",
                            }[chip.tone] || "text-stone-700 border-stone-200 bg-white hover:bg-stone-100";
                            return (
                                <Link
                                    key={ci}
                                    to={chip.path}
                                    className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${tone}`}
                                    data-testid={`copilot-chip-action-${ci}`}
                                >
                                    {chip.label} <ChevronRight size={11} />
                                </Link>
                            );
                        })}
                    </div>
                )}
                {!isUser && followups && followups.length > 0 && (
                    <div className="mt-3 border-t border-stone-100 pt-2.5" data-testid="copilot-followups">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 mb-1.5">Ask more…</div>
                        <div className="flex flex-wrap gap-1.5">
                            {followups.map((fu, fi) => (
                                <button
                                    key={fi}
                                    onClick={() => onFollowup && onFollowup(fu)}
                                    className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100"
                                    data-testid={`copilot-followup-${fi}`}
                                >
                                    {fu}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {isUser && (
                <div className="w-7 h-7 rounded-full bg-stone-200 text-stone-700 flex items-center justify-center shrink-0">
                    <UserIcon size={13} strokeWidth={1.8} />
                </div>
            )}
        </div>
    );
}
